import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ArrowLeft, Save, AlertTriangle, Camera, FileText, X, Image as ImageIcon, Video as VideoIcon } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TICKET_STATUSES, TICKET_SUPPORT_TYPES } from "../lib/enums";
import VoiceTextarea from "../components/VoiceTextarea";
import SearchableSelect from "../components/SearchableSelect";
import { uploadFile, uploadUrl } from "../lib/uploads";

/**
 * Full-page New Ticket editor — shared by staff (/tickets/new) AND the
 * customer portal (/portal/tickets/new). We never use minimal modal layers
 * for ticket logging anymore (Trust Contract). The same nice editor with
 * customer + site, subject, voice-dictation description, status / priority
 * / assignee, and photo/video/document uploads runs everywhere.
 *
 *  Routing behaviour:
 *   - Path `/portal/tickets/new` → portal layout: locked customer, hidden
 *     assignee, back links to /portal, success → /portal/tickets/{id}.
 *   - Path `/tickets/new` → staff layout: full editor as before.
 *
 *  Attachments flow:
 *   - Files are uploaded immediately via `uploadFile()` (background upload
 *     manager). Each upload returns metadata; we keep that list and pass it
 *     as `attachments` on POST /tickets (TicketIn accepts it directly).
 */
export default function NewTicket() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();

  // Portal context — either the path is /portal/... or the user is a client.
  const onPortalRoute = location.pathname.startsWith("/portal/");
  const isClient = user?.role === "client";
  const isPortal = isClient || onPortalRoute;

  const backHref = isPortal ? "/portal" : "/tickets";

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);

  const initialCustomerId = isClient
    ? (user?.customer_id || "")
    : (params.get("customer_id") || "");

  const [customerId, setCustomerId] = useState(initialCustomerId);
  const [siteId, setSiteId] = useState("");
  const [subject, setSubject] = useState(params.get("subject") || "");
  const [description, setDescription] = useState(params.get("description") || "");
  const [priority, setPriority] = useState("normal");
  const [status, setStatus] = useState("logged");
  const [supportType, setSupportType] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  // Iter 400 — plant SLA tracking. Ticked by the person logging the
  // ticket if the plant is currently down. Fires a plant_status_events
  // "down" row server-side so the SLA rollup counts this outage.
  const [plantDown, setPlantDown] = useState(false);

  // Attachments are uploaded immediately; we hold metadata only.
  const [attachments, setAttachments] = useState([]); // [{id, filename, content_type, size, kind}]
  const [uploading, setUploading] = useState({ done: 0, total: 0 });

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        // Portal clients are blocked from /api/customers (collection) by the
        // server's CRM-endpoint gate. They CAN reach /api/customers/{cid} —
        // their own record only. Staff still fetch the full list for the
        // searchable picker.
        const cReq = isClient && user?.customer_id
          ? api.get(`/customers/${user.customer_id}`).then((r) => ({ data: [r.data] })).catch(() => ({ data: [] }))
          : api.get("/customers");
        const reqs = [cReq, api.get("/sites")];
        if (!isPortal) reqs.push(api.get("/users").catch(() => ({ data: [] })));
        const [c, s, u] = await Promise.all(reqs);
        setCustomers(Array.isArray(c.data) ? c.data : (c.data ? [c.data] : []));
        setSites(s.data || []);
        if (!isPortal) setUsers((u?.data || []).filter((x) => x.role && x.role !== "client" && x.active !== false));
      } catch (e) { /* swallow */ }
    })();
  }, [isPortal, isClient, user?.customer_id]);

  // Site list is filtered by chosen customer
  const customerSites = useMemo(
    () => sites.filter((s) => s.customer_id === customerId),
    [sites, customerId],
  );

  // Auto-select if customer has exactly 1 site
  useEffect(() => {
    if (customerId && customerSites.length === 1) {
      setSiteId(customerSites[0].id);
    } else if (customerId && !customerSites.find((s) => s.id === siteId)) {
      setSiteId("");
    }
  }, [customerId, customerSites, siteId]);

  const customerOptions = useMemo(() => {
    const list = isClient && user?.customer_id
      ? customers.filter((c) => c.id === user.customer_id)
      : customers;
    return list.map((c) => ({
      id: c.id,
      label: c.name,
      sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email]
        .filter(Boolean).join(" · "),
      searchTerms: [
        c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp,
        c.email, c.physical_address, c.billing_address, c.industry,
        c.company_registration, c.legal_name, c.short_id,
      ].filter(Boolean),
    }));
  }, [customers, isClient, user?.customer_id]);

  const siteOptions = useMemo(
    () => customerSites.map((s) => ({
      id: s.id,
      label: s.name,
      sublabel: s.address || "",
      searchTerms: [s.name, s.address, s.external_ref].filter(Boolean),
    })),
    [customerSites],
  );

  // Portal users without any site on file can still submit — the backend
  // accepts an empty site (we'll pass through whichever site_id is picked,
  // or an empty string which the tenant will tidy up later).
  const siteRequired = !isPortal || customerSites.length > 0;
  const canSubmit = !!customerId
    && (!siteRequired || !!siteId)
    && subject.trim().length > 0
    && !busy
    && uploading.total === 0;

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading({ done: 0, total: files.length });
    let i = 0;
    for (const f of files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const up = await uploadFile(f);
        setAttachments((prev) => [
          ...prev,
          {
            id: up.id,
            filename: up.filename,
            content_type: up.content_type,
            size: up.size,
            kind: up.kind || "file",
          },
        ]);
      } catch (e2) {
        setErr(formatErr(e2?.response?.data?.detail, "Upload failed"));
      }
      i += 1;
      setUploading({ done: i, total: files.length });
    }
    setUploading({ done: 0, total: 0 });
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!customerId) { setErr("Please pick a customer."); return; }
    if (siteRequired && !siteId) { setErr("Please pick a site."); return; }
    if (!subject.trim()) { setErr("Subject is required."); return; }
    setBusy(true);
    setErr("");
    try {
      const payload = {
        customer_id: customerId,
        site_id: siteId,
        subject: subject.trim(),
        description: description.trim() || "",
        priority,
        status,
        support_type: supportType || null,
        assigned_to_id: assignedToId || null,
        attachments,
        plant_down: !!plantDown,
      };
      const { data } = await api.post("/tickets", payload);
      const tid = data.id;
      navigate(isPortal ? `/portal/tickets/${tid}` : `/tickets/${tid}`);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail, "Could not create ticket."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="new-ticket-page">
      <Link
        to={backHref}
        className="text-xs text-[#1fb6c1] hover:underline inline-flex items-center gap-1"
        data-testid="new-ticket-back"
      >
        <ArrowLeft className="w-3 h-3" /> {isPortal ? "Back to your portal" : "Back to Tickets"}
      </Link>

      <div className="hv-card p-5 md:p-6 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#9aa3b5]">Open a support ticket</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-0.5">
            {isPortal ? "Log a support ticket" : "New ticket"}
          </h1>
          <p className="text-sm text-[var(--hv-text-secondary)] mt-1">
            {isPortal
              ? "Tell us what's happening. Add photos, a voice note, or a short video — whatever helps us understand the problem fastest."
              : "Tickets are always linked to a customer + site. Pick those first, then fill in the details."}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="new-ticket-form">
          {/* Customer + Site selectors */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">
                {isClient ? "Customer" : "Customer *"}
              </label>
              {isClient ? (
                <PortalCustomerLockedField
                  customer={customers.find((c) => c.id === user?.customer_id) || null}
                />
              ) : (
                <SearchableSelect
                  value={customerId}
                  onChange={(id) => { setCustomerId(id); setSiteId(""); }}
                  options={customerOptions}
                  placeholder="— pick a customer —"
                  required
                  testId="new-ticket-customer"
                  emptyHint="No customers match. Try VAT, account number, phone or email."
                  onCreateNew={(q) => {
                    navigate(`/customers/new?name=${encodeURIComponent(q || "")}&from=tickets/new`);
                  }}
                />
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">
                Site{siteRequired ? " *" : " (optional)"}
              </label>
              <SearchableSelect
                value={siteId}
                onChange={setSiteId}
                options={siteOptions}
                placeholder={
                  !customerId
                    ? "— pick a customer first —"
                    : customerSites.length === 0
                      ? "— no sites on file —"
                      : "— pick a site —"
                }
                disabled={!customerId || customerSites.length === 0}
                required={siteRequired}
                testId="new-ticket-site"
                emptyHint="No sites match this search."
                onCreateNew={customerId && !isPortal ? () => {
                  navigate(`/customers/${customerId}?focus=sites&from=tickets/new`);
                } : undefined}
                createNewLabel={customerId ? "Add a site to this customer" : undefined}
              />
              {customerId && customerSites.length === 0 && !isPortal && (
                <div className="text-[11px] text-amber-300 mt-1">
                  This customer has no registered sites yet.{" "}
                  <Link to={`/customers/${customerId}`} className="underline">Add a site first</Link>.
                </div>
              )}
              {customerId && customerSites.length === 0 && isPortal && (
                <div className="text-[11px] text-[var(--hv-text-muted)] mt-1">
                  No sites on file — our team will reach out to confirm the location.
                </div>
              )}
              {customerId && customerSites.length === 1 && siteId && (
                <div className="text-[10px] text-[#1fb6c1] mt-1">
                  Auto-selected (only one site).
                </div>
              )}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5] block mb-1">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Pump tripping out, RO membrane fouled, no soft water…"
              required
              className="hv-input text-base w-full"
              data-testid="new-ticket-subject"
              autoFocus={!isClient}
            />
          </div>

          {/* Description with voice */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Description</label>
            <VoiceTextarea
              value={description}
              onChange={setDescription}
              rows={6}
              placeholder="What's happening, when did it start, anything you've tried, urgency context… Or tap the mic to dictate."
              className="text-sm w-full"
              context="ticket_description"
              testIdPrefix="new-ticket-desc-voice"
              data-testid="new-ticket-description"
            />
          </div>

          {/* Priority + Status + Support type (staff sees all; portal sees priority only) */}
          {!isPortal ? (
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)} className="hv-input mt-1 text-sm w-full" data-testid="new-ticket-priority">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="hv-input mt-1 text-sm w-full" data-testid="new-ticket-status">
                  {TICKET_STATUSES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Support type</label>
                <select value={supportType} onChange={(e) => setSupportType(e.target.value)} className="hv-input mt-1 text-sm w-full" data-testid="new-ticket-support-type">
                  <option value="">— optional —</option>
                  {TICKET_SUPPORT_TYPES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">How urgent is this?</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="hv-input mt-1 text-sm w-full sm:max-w-xs" data-testid="new-ticket-priority">
                <option value="low">Low — when you can</option>
                <option value="normal">Normal — soon</option>
                <option value="high">High — today</option>
                <option value="urgent">Urgent — system down</option>
              </select>
            </div>
          )}

          {/* Iter 400 — Plant status flag. Ticking this on ticket
              creation writes a plant_status_events "down" row for the
              linked site so the SLA rollup counts this outage from
              now. On resolution the tech will be prompted to confirm
              the plant is back up. */}
          <label
            className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer select-none ${
              plantDown
                ? "border-rose-400/40 bg-rose-500/[0.08]"
                : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
            }`}
            data-testid="new-ticket-plant-down-toggle"
          >
            <input
              type="checkbox"
              checked={plantDown}
              onChange={(e) => setPlantDown(e.target.checked)}
              className="mt-1 accent-rose-400"
              data-testid="new-ticket-plant-down-checkbox"
            />
            <span className="flex-1">
              <span className={`font-semibold ${plantDown ? "text-rose-200" : "text-white"}`}>
                The plant is currently DOWN
              </span>
              <span className="block text-[11px] text-[#a0aabf] mt-0.5">
                Tick this only if the site is not producing water right now.
                The uptime counter will start now and stop when the resolving
                tech confirms the plant is back up.
              </span>
            </span>
          </label>

          {/* Assignee — staff only */}
          {!isPortal && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Assign to</label>
              <select
                value={assignedToId}
                onChange={(e) => setAssignedToId(e.target.value)}
                className="hv-input mt-1 text-sm w-full"
                data-testid="new-ticket-assignee"
              >
                <option value="">— unassigned —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}{u.role ? ` · ${u.role}` : ""}
                  </option>
                ))}
              </select>
              {assignedToId && (
                <div className="text-[11px] text-[#1fb6c1] mt-1">
                  They'll get a notification + an urgent task on their workboard.
                </div>
              )}
            </div>
          )}

          {/* Media upload — uploads immediately so submit is instant */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">
                Photos · videos · documents (optional)
              </label>
              {uploading.total > 0 && (
                <span className="text-[10px] text-[#1fb6c1]" data-testid="new-ticket-upload-progress">
                  Uploading {uploading.done}/{uploading.total}…
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <label className="hv-btn hv-btn-ghost text-xs cursor-pointer" data-testid="new-ticket-attach-camera">
                <Camera className="w-4 h-4 text-[#1fb6c1]" /> Take photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={onPickFiles}
                  className="hidden"
                />
              </label>
              <label className="hv-btn hv-btn-ghost text-xs cursor-pointer" data-testid="new-ticket-attach-files">
                <FileText className="w-4 h-4 text-[#A78BFA]" /> Add files
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,application/pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
                  onChange={onPickFiles}
                  className="hidden"
                />
              </label>
              <span className="text-[11px] text-[var(--hv-text-muted)] italic">
                Each file is saved as you add it.
              </span>
            </div>
            {attachments.length > 0 && (
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="new-ticket-attachments">
                {attachments.map((a) => (
                  <AttachmentChip key={a.id} att={a} onRemove={() => removeAttachment(a.id)} />
                ))}
              </div>
            )}
          </div>

          {/* Comments hint */}
          <div className="text-[11px] text-[var(--hv-text-muted)] italic border-l-2 border-[#1fb6c1]/30 pl-2">
            Comments will be available once the ticket is {isPortal ? "logged" : "created"}.
          </div>

          {/* Errors */}
          {err && (
            <div className="text-sm text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded px-3 py-2 flex items-start gap-2" data-testid="new-ticket-err">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--hv-border)]">
            <Link to={backHref} className="hv-btn hv-btn-ghost" data-testid="new-ticket-cancel">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={!canSubmit}
              className="hv-btn hv-btn-primary"
              data-testid="new-ticket-submit"
            >
              <Save className="w-4 h-4" /> {busy ? (isPortal ? "Sending…" : "Creating…") : (isPortal ? "Send ticket" : "Create ticket")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Read-only customer field for portal clients. Never a picker — the client
 * is logged in as exactly one customer and must never see (or be able to
 * pick) any other tenant's customers. Shows the customer's name, account
 * ref / VAT as subtitle, and a small "from your portal account" hint.
 */
function PortalCustomerLockedField({ customer }) {
  return (
    <div
      className="hv-input mt-1 text-sm w-full opacity-90 cursor-not-allowed flex items-center justify-between gap-2 px-3"
      data-testid="new-ticket-customer-locked"
      aria-readonly="true"
    >
      <div className="min-w-0">
        <div className="truncate font-medium text-[var(--hv-text-primary)]">
          {customer?.name || <span className="text-[var(--hv-text-muted)]">Loading your account…</span>}
        </div>
        {customer && (customer.external_ref || customer.vat_number) && (
          <div className="truncate text-[10px] text-[var(--hv-text-muted)]">
            {[customer.external_ref && `Acc# ${customer.external_ref}`, customer.vat_number && `VAT ${customer.vat_number}`]
              .filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
      <span className="text-[9px] uppercase tracking-wider text-[#1fb6c1] shrink-0">Locked</span>
    </div>
  );
}


function AttachmentChip({ att, onRemove }) {
  const url = uploadUrl(att.id);
  const ct = att.content_type || "";
  const isImage = ct.startsWith("image/") || att.kind === "image";
  const isVideo = ct.startsWith("video/") || att.kind === "video";
  return (
    <div className="relative group rounded border border-[var(--hv-border)] overflow-hidden bg-black/30 aspect-square">
      {isImage ? (
        <img src={url} alt={att.filename} className="w-full h-full object-cover" />
      ) : isVideo ? (
        <video src={url} className="w-full h-full object-cover bg-black" muted preload="metadata" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-[var(--hv-text-muted)] p-1 text-center">
          <FileText className="w-5 h-5" />
          <span className="text-[9px] truncate w-full" title={att.filename}>{att.filename}</span>
        </div>
      )}
      {isVideo && (
        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] uppercase rounded px-1">
          <VideoIcon className="w-3 h-3 inline" />
        </span>
      )}
      {isImage && (
        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] uppercase rounded px-1">
          <ImageIcon className="w-3 h-3 inline" />
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 bg-black/70 text-rose-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
        title="Remove"
        data-testid={`new-ticket-attach-remove-${att.id}`}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
